import type { AsyncDataOptions, NuxtApp } from 'nuxt/app'
import { THIRTY_MINUTES_IN_SECONDS } from '../constants/time'

const CACHE_KEY_PREFIX = 'MonitoStorage.'
const DEFAULT_CACHE = THIRTY_MINUTES_IN_SECONDS

function cacheKey(key: string) {
  return `${CACHE_KEY_PREFIX}${key}`
}

function readStorage(key: string): unknown {
  if (import.meta.client) {
    const stored = localStorage.getItem(cacheKey(key))
    return stored ? JSON.parse(stored) : undefined
  }
}

function writeStorage(key: string, value: unknown) {
  if (import.meta.client) {
    if (value === null) {
      localStorage.removeItem(cacheKey(key))
    } else {
      localStorage.setItem(cacheKey(key), JSON.stringify(value))
    }
  }
}

function addCacheInfo<T>(input: T, version?: string) {
  return {
    ...input,
    fetchedAt: new Date().toISOString(),
    version,
  }
}

function ttlCache(ttl: number, version?: string) {
  return (key: string) => {
    const nuxtApp = useNuxtApp()
    const data = nuxtApp.payload.data[key] ?? readStorage(key)
    if (!data) return

    const expirationDate = new Date(data.fetchedAt)
    expirationDate.setTime(expirationDate.getTime() + ttl * 1000)
    if (expirationDate.getTime() < Date.now() || data.version !== version) {
      writeStorage(key, null)
      return
    }

    useLogger('composables:useAsyncCachedData:ttlCache').debug(
      { key, data },
      'returning cached data',
    )
    return data
  }
}

export default async function useAsyncCachedData<Data>(
  key: string,
  handler: (ctx?: NuxtApp) => Promise<Data>,
  options?: AsyncDataOptions<Data> & {
    ttl?: number
    persist?: boolean
  },
) {
  const { version } = useRuntimeConfig().public
  const result = useAsyncData<Data>(key, handler, {
    transform: <Data>(input: Data) => addCacheInfo<Data>(input, version),
    getCachedData: ttlCache(options?.ttl ?? DEFAULT_CACHE, version),
    deep: false,
    lazy: true,
    ...options,
  })

  if (options?.persist) {
    watch(
      result.data,
      () => {
        writeStorage(key, result.data.value)
      },
      { immediate: true },
    )
  }

  return result
}
